import { useEffect, useRef, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

const IDLE_MIN = 15;
const WARN_MIN = 13;

// Idle-timeout warning + step-up re-auth prompt (mounted once inside the logged-in layout).
export const SessionGuard = ({ onLogout }) => {
  const [warn, setWarn] = useState(false);
  const [step, setStep] = useState(null); // {resolve, reject}
  const [pw, setPw] = useState('');
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const loggedOut = useRef(false);

  useEffect(() => {
    const t = setInterval(() => {
      const idle = (Date.now() - Number(sessionStorage.getItem('kv_last_api') || Date.now())) / 60000;
      if (idle >= IDLE_MIN && !loggedOut.current) {
        loggedOut.current = true;
        api.post('/api/auth/logout').catch(() => {}).finally(() => onLogout('Signed out after 15 minutes of inactivity'));
      } else setWarn(idle >= WARN_MIN);
    }, 15000);
    const onStep = (e) => { setErr(''); setPw(''); setCode(''); setStep(e.detail); };
    window.addEventListener('kv-step-up', onStep);
    return () => { clearInterval(t); window.removeEventListener('kv-step-up', onStep); };
  }, [onLogout]);

  const submit = async () => {
    try {
      await api.post('/api/auth/step-up', { password: pw, mfa_code: code || null });
      step.resolve(); setStep(null);
    } catch (e) { setErr(e.response?.data?.detail || 'Re-authentication failed'); }
  };
  return (<>
    <Dialog open={warn} onOpenChange={() => {}}>
      <DialogContent data-testid="idle-warning-dialog">
        <DialogHeader><DialogTitle>Are you still there?</DialogTitle>
          <DialogDescription>For security you'll be signed out after {IDLE_MIN} minutes of inactivity.</DialogDescription></DialogHeader>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => api.post('/api/auth/logout').finally(() => onLogout())} data-testid="idle-logout-btn">Sign out</Button>
          <Button onClick={() => api.get('/api/auth/session').then(() => setWarn(false))} data-testid="idle-stay-btn">Stay signed in</Button>
        </div>
      </DialogContent>
    </Dialog>
    <Dialog open={!!step} onOpenChange={(o) => { if (!o) { step?.reject(); setStep(null); } }}>
      <DialogContent className="max-w-sm" data-testid="step-up-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-primary" />Confirm it's you</DialogTitle>
          <DialogDescription>This is a sensitive action. Re-enter your password (and authenticator code if you use 2FA).</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Password</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus data-testid="step-up-password" /></div>
          <div><Label>Authenticator or backup code</Label><Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Optional if 2FA is off" data-testid="step-up-code" /></div>
          {err && <p className="text-sm text-destructive" data-testid="step-up-error">{err}</p>}
          <Button className="w-full" onClick={submit} disabled={!pw} data-testid="step-up-submit">Confirm</Button>
        </div>
      </DialogContent>
    </Dialog>
  </>);
};
